import { PolicyValueType } from 'components/PolicyFilterDropdown';
import { atom, useAtom } from 'jotai';
import { atomWithReset, atomWithStorage, useResetAtom } from 'jotai/utils';
import { showInfoToast } from 'lib/ToastHelper';
import { companyIdAtom } from 'views/state';
import { DEFAULT_PAGE_SIZE } from 'views/types';

/**
 * Atom to store the selected source type (asset type) for PCDashboard AssetListing page
 * Default: 'ca_data' (Cloud Agents - All)
 */
export const sourceTypeAtom = atomWithStorage<string>(
  'pc_assets_source_type',
  'ca_data',
);

/**
 * Atom to store the selected agent key for Cloud Agents in PCDashboard AssetListing page
 */
export const agentKeyAtom = atomWithStorage<string>('pc_assets_agent_key', '');

export const DEFAULT_COLUMN_KEYS_ASSET = [
  // 'id',
  'name',
  'alias',
  // 'host_id',
  'os',
  'address',
  'last_compliance_scan',
  'failed_count',
  'passed_count',
  'compliance',
  'actions',
];

export const componentHeightAtom = atom(0);

export const columnKeysAssetAtom = atomWithStorage(
  'asset_listing_active_column_keys',
  DEFAULT_COLUMN_KEYS_ASSET,
);

export const filterValuesAtom = atomWithStorage<Record<string, any>>(
  'asset_listing_filter_values_atom',
  {},
);

const DateFilterKeys = [
  'ca_modified',
  'last_checked_in',
  'last_compliance_scan',
];

export const parsedFilterValuesAtom = atom<Record<string, any>>((get) => {
  const filterValues = get(filterValuesAtom);
  const updatedFilterValues = {
    ...filterValues,
    ...DateFilterKeys.reduce((acc, key) => {
      if (filterValues[key]) {
        acc[key] = filterValues[key].map((dateRange) => ({
          from: dateRange.from ? new Date(dateRange.from) : null,
          to: dateRange.to ? new Date(dateRange.to) : null,
        }));
      }
      return acc;
    }, {}),
  };
  return updatedFilterValues;
});

export const selectedRowIdsAtom = atomWithReset<number[]>([]);

export const forceRerenderAssetGridAtom = atom<string>('');
export const assetDataRefreshAtom = atomWithStorage<string>(
  'asset_refresh',
  '',
);

export const paginationConfigAtom = atomWithStorage(
  'pc_dashboard_assets_pagination_config',
  {
    page_size: DEFAULT_PAGE_SIZE,
    page: 1,
  },
);
export const orderbyAtom = atomWithReset<string>('host_name');
export const sortOrderAtom = atomWithReset<'desc' | 'asc'>('asc');

export const policyIdFilterAtom = atomWithStorage<PolicyValueType>(
  'pc_dashboard_assets_policy_filter',
  'all',
);

export const isQuerystringFiltersInitializedAtom = atom<boolean>(true);

/**
 * Base query params atom containing only company, policy, and filter values.
 * Does NOT include pagination (page, page_size) or ordering.
 * Used for the insights API which returns aggregated data unaffected by page/sort.
 */
export const assetInsightsQueryParamsAtom = atom(async (get) => {
  const companyId = get(companyIdAtom);
  const policyId = get(policyIdFilterAtom);
  const policyIdFilter = policyId === 'all' ? undefined : policyId;
  const filterValues = get(parsedFilterValuesAtom);
  const _assetDataRefresh = get(assetDataRefreshAtom);
  const sourceType = get(sourceTypeAtom);
  const agentKey = get(agentKeyAtom);

  if (companyId && companyId !== -1) {
    const params: Record<string, any> = {
      company_id: companyId.toString(),
    };

    if (policyIdFilter) {
      params['policy_id'] = policyIdFilter.toString();
    }

    // source type filter
    // split the source type value at '__' and use the first part as the source type
    // which is the 2nd part as type of the ep and ca hosts (e.g. internal, external, servers, workstations etc.)
    if (sourceType) {
      const [sourceTypeValue, subType] = sourceType.split('__');
      params['source_type'] = sourceTypeValue;
      if (sourceTypeValue === 'endpoint_data' && subType) {
        params['scanner_type'] = subType;
      } else if (sourceTypeValue === 'ca_data' && subType) {
        params['ca_type'] = subType;
        if (agentKey !== '') {
          params['ca_agent_key'] = agentKey;
        }
      }
    }

    Object.entries(filterValues).forEach(([key, value]) => {
      if (value && value.length > 0) {
        if (DateFilterKeys.includes(key)) {
          const fromDate = value[0]?.from;
          const toDate = value[0]?.to;
          if (fromDate instanceof Date) {
            params[`${key}_after`] = fromDate.toISOString();
          } else {
            console.error({
              msg: 'fromDate is not a valid Date object:',
              fromDate,
            });
          }
          if (toDate) params[`${key}_before`] = toDate?.toISOString();
        } else {
          params[key] = value.join(',');
        }
      }
    });

    return params;
  }
  return undefined;
});

/**
 * Full query params atom for the grid listing API.
 * Derives from assetInsightsQueryParamsAtom and adds pagination and ordering.
 * Re-evaluates on page change, sort change, or any filter change.
 */
export const assetDataQueryParamsAtom = atom(async (get) => {
  const baseParams = get(assetInsightsQueryParamsAtom);
  if (!baseParams) return undefined;

  const paginationConfig = get(paginationConfigAtom);
  const orderBy = get(orderbyAtom);
  const order = get(sortOrderAtom);
  const ordering = orderBy
    .split(',')
    .map(
      (field) =>
        `${order === 'desc' && !field.startsWith('-') ? '-' : ''}${field}`,
    )
    .join(',');

  return {
    ...baseParams,
    page: paginationConfig.page.toString(),
    page_size: paginationConfig.page_size.toString(),
    ordering: ordering,
  };
});

// Define the structure for insights data
interface AssetInsights {
  total: number;
  total_failed_postures: number;
  total_passed_postures: number;
  unique_os_count: number;
  unique_policy_count: number;
  urgent: number;
  critical: number;
  serious: number;
  medium: number;
  minimal: number;
}

// Separate atom to store insights data for assets
export const assetInsightsAtom = atom({
  insights: {} as AssetInsights,
  loading: false,
});

// Update assetDataAtom to only include results and count
export const assetDataAtom = atom({
  results: [] as any[],
  count: 0,
  loading: false,
});

// Add a new atom to store vulnerability counts for checked hosts
export const checkedHostsFindingsMapAtom = atom<Record<number, number>>({});

export const findingsCountForCheckedHostsAtom = atom(async (get) => {
  // Fetching checkedHostIds and allHosts using atoms
  const checkedHostIds = get(selectedRowIdsAtom);
  const findingsMap = get(checkedHostsFindingsMapAtom);
  let total = 0;
  if (checkedHostIds?.length > 0) {
    const allHosts = get(assetDataAtom);
    // NOTE: get the checked hosts based on checkedHostIds
    // NOTE: loop through all checked hosts, and calculate the grand total of findings
    // Getting the checked hosts based on checkedHostIds
    const currentPageHosts = allHosts?.results?.filter((host) =>
      checkedHostIds.includes(host.host_id),
    );

    // Calculate total using both stored and current page values
    checkedHostIds.forEach((hostId) => {
      // Use stored values if available, otherwise 0
      const hostFindings = findingsMap[hostId] || 0;
      total += hostFindings || 0;
    });

    currentPageHosts?.forEach((host) => {
      const totalHostFindings = host.passed_count + host.failed_count;
      if (totalHostFindings !== findingsMap[host.host_id]) {
        total = total - findingsMap[host.host_id] + totalHostFindings;
      }
    });
  }
  return total;
});

export function useOnFilterChangeResetRelatedAtoms() {
  const resetSelectedRowIdsAtom = useResetAtom(selectedRowIdsAtom);
  const resetOrderbyAtom = useResetAtom(orderbyAtom);
  // const resetSortOrderAtom = useResetAtom(sortOrderAtom);
  const [paginationConfig, setPaginationConfig] = useAtom(paginationConfigAtom);

  const resetAtoms = () => {
    resetSelectedRowIdsAtom();
    resetOrderbyAtom();
    // resetSortOrderAtom();
    setPaginationConfig({
      ...paginationConfig,
      page: 1,
    });
  };

  return resetAtoms;
}

/**
 * Hook to reset relevant atoms when the policy filter changes
 * @returns a function that resets the selectedRowIdsAtom, orderbyAtom, sortOrderAtom, and paginationConfigAtom
 */
export const useOnPolicyFilterChangeResetRelatedAtoms = () => {
  const resetAtoms = useOnFilterChangeResetRelatedAtoms();
  const resetFilterValuesAtom = useResetAtom(filterValuesAtom);
  return () => {
    resetAtoms();
    resetFilterValuesAtom();
  };
};

/**
 * Hook to reset relevant atoms when the source type (HostTypeDropdown) changes
 * @returns a function that resets the relevant atoms
 */
export function useOnSourceTypeChangeAssetsResetAtoms() {
  const resetOnFilterChangesResetAtoms = useOnFilterChangeResetRelatedAtoms();
  const [filterValues, setFilterValues] = useAtom(filterValuesAtom);

  const resetAtoms = () => {
    resetOnFilterChangesResetAtoms();
    const resetFilterNames: string[] = [];

    // Check which filters are currently applied
    if (filterValues.search && filterValues.search.length > 0) {
      resetFilterNames.push('Search');
    }
    if (filterValues.os && filterValues.os.length > 0) {
      resetFilterNames.push('OS');
    }
    if (filterValues.ip && filterValues.ip.length > 0) {
      resetFilterNames.push('IP Address');
    }
    if (filterValues.scan_status && filterValues.scan_status.length > 0) {
      resetFilterNames.push('Scan Status');
    }
    if (
      filterValues.findings_status &&
      filterValues.findings_status.length > 0
    ) {
      resetFilterNames.push('Findings Status');
    }
    if (filterValues.ca_modified && filterValues.ca_modified.length > 0) {
      resetFilterNames.push('CA Modified');
    }
    if (
      filterValues.last_checked_in &&
      filterValues.last_checked_in.length > 0
    ) {
      resetFilterNames.push('Last Checked In');
    }
    if (
      filterValues.last_compliance_scan &&
      filterValues.last_compliance_scan.length > 0
    ) {
      resetFilterNames.push('Last Compliance Scan');
    }

    // Reset all filters
    setFilterValues({});

    // Only show toast if filters were actually reset
    if (resetFilterNames.length > 0) {
      showInfoToast(
        `${resetFilterNames.join(', ')} filter${
          resetFilterNames.length > 1 ? 's' : ''
        } ${
          resetFilterNames.length > 1 ? 'have' : 'has'
        } been reset due to Asset Type filter change. Please reapply your filter selection${
          resetFilterNames.length > 1 ? 's' : ''
        } if needed.`,
      );
    }
  };

  return resetAtoms;
}
